import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  backupService,
  CreateBackupPayload,
  RestorePayload,
  SaveDestinationPayload,
  UpdateSchedulePayload,
} from '../backupService';
import { BackupProvider, RestoreUploadPayload } from '../../../types/backup';

const BACKUP_KEYS = {
  overview: ['backup', 'overview'] as const,
  records: ['backup', 'records'] as const,
  destinations: ['backup', 'destinations'] as const,
  schedule: ['backup', 'schedule'] as const,
  modules: ['backup', 'modules'] as const,
  encryption: ['backup', 'encryption'] as const,
};

export function useBackupOverview() {
  return useQuery({
    queryKey: BACKUP_KEYS.overview,
    queryFn: () => backupService.getOverview(),
  });
}

export function useBackups(params?: {
  page?: number;
  limit?: number;
  status?: string;
}) {
  return useQuery({
    queryKey: [...BACKUP_KEYS.records, params],
    queryFn: () => backupService.listBackups(params),
    refetchInterval: (query) => {
      const records = query.state.data?.data;
      // Keep polling while a run is still in flight so a queued job updates
      // itself to running/completed without a manual refresh.
      const inProgress = records?.some(
        (record) =>
          record.status === 'queued' ||
          record.status === 'pending' ||
          record.status === 'running',
      );
      return inProgress ? 3000 : false;
    },
  });
}

export function useBackupDestinations() {
  return useQuery({
    queryKey: BACKUP_KEYS.destinations,
    queryFn: () => backupService.listDestinations(),
  });
}

/**
 * Scans the configured destinations for archives missing from the history
 * (for example backups uploaded by another server to the same WebDAV folder).
 */
export function useSyncDestinations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (destinationId?: string) =>
      backupService.syncDestinations(destinationId),
    onSuccess: () => invalidateAll(queryClient),
    // The history tab scans storage automatically on mount and the page shows
    // its own "imported/already listed" toast, so never fall back to the
    // generic "Saved successfully" message for a background scan.
    meta: { hideToast: true },
  });
}

export function useBackupSchedule() {
  return useQuery({
    queryKey: BACKUP_KEYS.schedule,
    queryFn: () => backupService.getSchedule(),
  });
}

export function useBackupModules() {
  return useQuery({
    queryKey: BACKUP_KEYS.modules,
    queryFn: () => backupService.listDataModules(),
    staleTime: 300000,
  });
}

export function useBackupOAuthStatus() {
  return useQuery({
    queryKey: ['backup', 'oauth-status'],
    queryFn: () => backupService.getOAuthStatus(),
    staleTime: 300000,
  });
}

function invalidateAll(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['backup'] });
}

export function useCreateBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBackupPayload) =>
      backupService.createBackup(payload),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useDeleteBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => backupService.deleteBackup(id),
    onSuccess: () => invalidateAll(queryClient),
  });
}

/** Deletes several archives at once (history table bulk action). */
export function useDeleteBackups() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => backupService.deleteBackups(ids),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useVerifyBackup() {
  return useMutation({
    mutationFn: (id: string) => backupService.verifyBackup(id),
  });
}

export function useRestoreBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RestorePayload }) =>
      backupService.restoreBackup(id, payload),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useInspectRestoreUpload() {
  return useMutation({
    mutationFn: (file: File) => backupService.inspectRestoreUpload(file),
  });
}

export function useRestoreUpload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RestoreUploadPayload) =>
      backupService.restoreUpload(payload),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useSaveDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      provider,
      payload,
    }: {
      provider: BackupProvider;
      payload: SaveDestinationPayload;
    }) => backupService.saveDestination(provider, payload),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useTestDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      provider,
      config,
    }: {
      provider: BackupProvider;
      config?: Record<string, any>;
    }) => backupService.testDestination(provider, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BACKUP_KEYS.destinations });
    },
  });
}

export function useDeleteDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider: BackupProvider) =>
      backupService.deleteDestination(provider),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateSchedulePayload) =>
      backupService.updateSchedule(payload),
    onSuccess: () => invalidateAll(queryClient),
  });
}

/**
 * Starts the saved schedule now. Unlike `useCreateBackup` the run goes through
 * the scheduled pipeline (trigger `scheduled`, schedule stats + retention).
 */
export function useRunScheduleNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => backupService.runScheduleNow(),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useEncryptionStatus() {
  return useQuery({
    queryKey: BACKUP_KEYS.encryption,
    queryFn: () => backupService.getEncryptionStatus(),
    staleTime: 60_000,
  });
}

/** Re-wraps one archive with the server's current encryption key. */
export function useReencryptBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => backupService.reencryptBackup(id),
    onSuccess: () => invalidateAll(queryClient),
  });
}

/** Dry-run of a stored backup restore (no mutation, no safety backup). */
export function usePreviewRestore() {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RestorePayload }) =>
      backupService.previewRestore(id, payload),
  });
}

/** Dry-run of an uploaded archive restore. */
export function usePreviewUploadRestore() {
  return useMutation({
    mutationFn: (payload: RestoreUploadPayload) =>
      backupService.previewUploadRestore(payload),
  });
}

/**
 * Polls a backup run's live progress. Stops polling once the job reaches a
 * terminal state so idle pages don't hammer the API.
 */
export function useBackupRun(id: string | null, live = false) {
  return useQuery({
    queryKey: ['backup', 'run', id],
    queryFn: () => backupService.getRunProgress(id as string),
    enabled: !!id,
    refetchInterval: (query) => {
      // With a connected socket the progress arrives as an event; polling is
      // only the fallback when WebSockets are unavailable.
      if (live) return false;
      const state = query.state.data?.data?.state;
      const active =
        !!state &&
        !['completed', 'failed', 'cancelled', 'unknown'].includes(state);
      return active ? 1000 : false;
    },
  });
}

export function usePauseBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => backupService.pauseBackup(id),
    onSettled: (_d, _e, id) => {
      queryClient.invalidateQueries({ queryKey: ['backup', 'run', id] });
    },
  });
}

export function useResumeBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => backupService.resumeBackup(id),
    onSettled: (_d, _e, id) => {
      queryClient.invalidateQueries({ queryKey: ['backup', 'run', id] });
    },
  });
}

export function useCancelBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => backupService.cancelBackup(id),
    onSettled: (_d, _e, id) => {
      queryClient.invalidateQueries({ queryKey: ['backup', 'run', id] });
      invalidateAll(queryClient);
    },
  });
}

export function useDestroyJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => backupService.destroyJob(id),
    onSettled: () => invalidateAll(queryClient),
  });
}

/** Re-queues a failed backup job and resumes polling its progress. */
export function useRetryBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => backupService.retryBackup(id),
    onSettled: (_d, _e, id) => {
      queryClient.invalidateQueries({ queryKey: ['backup', 'run', id] });
      invalidateAll(queryClient);
    },
  });
}

export function useQueueStatus() {
  return useQuery({
    queryKey: ['backup', 'queue'],
    queryFn: () => backupService.getQueueStatus(),
    refetchInterval: 3000,
  });
}

export function usePauseQueue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => backupService.pauseQueue(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backup', 'queue'] }),
  });
}

export function useResumeQueue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => backupService.resumeQueue(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backup', 'queue'] }),
  });
}

/** Polls a restore job's live progress (stops once terminal). */
export function useRestoreRun(jobId: string | null, live = false) {
  return useQuery({
    queryKey: ['backup', 'restore', jobId],
    queryFn: () => backupService.getRestoreRun(jobId as string),
    enabled: !!jobId,
    refetchInterval: (query) => {
      if (live) return false;
      const state = query.state.data?.data?.state;
      const active =
        !!state &&
        !['completed', 'failed', 'cancelled', 'unknown'].includes(state);
      return active ? 1000 : false;
    },
  });
}

export function usePauseRestore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => backupService.pauseRestore(jobId),
    onSettled: (_d, _e, jobId) => {
      queryClient.invalidateQueries({ queryKey: ['backup', 'restore', jobId] });
    },
  });
}

export function useResumeRestore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => backupService.resumeRestore(jobId),
    onSettled: (_d, _e, jobId) => {
      queryClient.invalidateQueries({ queryKey: ['backup', 'restore', jobId] });
    },
  });
}

export function useCancelRestore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => backupService.cancelRestore(jobId),
    onSettled: (_d, _e, jobId) => {
      queryClient.invalidateQueries({ queryKey: ['backup', 'restore', jobId] });
      invalidateAll(queryClient);
    },
  });
}

export function useDestroyRestoreJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => backupService.destroyRestoreJob(jobId),
    onSettled: () => invalidateAll(queryClient),
  });
}

/** Re-queues a failed restore job and resumes polling its progress. */
export function useRetryRestore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => backupService.retryRestore(jobId),
    onSettled: (_d, _e, jobId) => {
      queryClient.invalidateQueries({ queryKey: ['backup', 'restore', jobId] });
      invalidateAll(queryClient);
    },
  });
}

export function useStorageHealth() {
  return useQuery({
    queryKey: ['backup', 'storage-health'],
    queryFn: () => backupService.getStorageHealth(),
    refetchInterval: 30000,
  });
}
